import { FeatureCollection, Feature } from "geojson";

import { BasicObject } from "../types";

function isFeatureCollection(
  data: Feature | FeatureCollection
): data is FeatureCollection {
  return data.type === "FeatureCollection";
}

function getFirstGroupingKey(
  features: Feature[],
  groupingKey?: string
): string | number | undefined {
  const [firstFeature] = features;
  if (groupingKey && firstFeature && firstFeature.properties) {
    return firstFeature.properties[groupingKey];
  }
  return undefined;
}

/**
 * Some properties are disguised as arrays... check and split the values!
 * We always return an array for easy processing the values.
 *
 * @param rawValue string
 * @returns string[]
 */
export function useWMSProperty(rawValue: string | number | null): string[] {
  if (rawValue === null) return [""];

  const value = rawValue.toString();
  const lastIndex = value.length - 1;
  if (value.charAt(0) === "[" && value.charAt(lastIndex) === "]") {
    const matches = value.substring(1, lastIndex).match(/((?:[^,]+))/gm);
    if (matches)
      return Array.from(matches.values()).map((value) => value.trim());
    return [];
  }

  return [value];
}

export function useProperties(
  data: Feature | FeatureCollection | undefined,
  groupingKey?: string
): BasicObject[] {
  if (data === undefined) return [];

  if (isFeatureCollection(data)) {
    if (data.features.length === 0) return [];
    const groupData = getFirstGroupingKey(data.features, groupingKey);
    return data.features.reduce((acc: BasicObject[], feature) => {
      if (
        groupData &&
        groupingKey &&
        feature.properties &&
        feature.properties[groupingKey] === groupData
      )
        return [...acc, { ...feature.properties }];
      if (!groupData && !groupingKey)
        return [...acc, { ...feature.properties }];
      return [...acc];
    }, []);
  }

  if (!isFeatureCollection(data)) {
    return [{ ...data.properties }];
  }

  return [];
}
